import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Grid,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Check,
  Close,
  MoreVert,
  AddCircleOutline,
  RemoveCircleOutline,
  HelpOutline,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateTime, formatNumber } from '../../utils/format';
import { ConfirmDialog } from '../../components/common/confirm';
import useFokusNachLaden from '../../components/common/hooks/useFokusNachLaden';
import TableSkeleton from '../../Skeletons/TableSkeleton';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

/**
 * Rules Engine Tab
 * Manage database-driven taxonomy rules with UI-configurable logic
 */
export default function RulesEngineTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // Dialog states
  const [ruleDialog, setRuleDialog] = useState({ open: false, mode: 'create', rule: null });
  const [testDialog, setTestDialog] = useState({ open: false, rule: null });
  const [logsDialog, setLogsDialog] = useState({ open: false, rule: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  // Die Regel, auf die die Loeschrueckfrage zeigt (K-D).
  const [pendingDeleteRule, setPendingDeleteRule] = useState(null);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/taxonomy/rules`);
      setRules(res.data.rules);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
      setError('Failed to load rules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 🔴 K-D (M-13): `window.confirm` ersetzt. Der Ausloeser ist das Papierkorb-
  //    Symbol einer Regelzeile, kein eigener Bestaetigungsknopf — deshalb
  //    `ConfirmDialog`. Der Text des `confirm` ist in Text und Ueberschrift
  //    aufgeteilt, nicht umformuliert.
  const handleDeleteRule = (ruleId, ruleName) => setPendingDeleteRule({ id: ruleId, name: ruleName });

  const confirmDeleteRule = async () => {
    const rule = pendingDeleteRule;
    setPendingDeleteRule(null);
    if (!rule) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/taxonomy/rules/${rule.id}`);
      fetchRules();
    } catch (err) {
      console.error('Failed to delete rule:', err);
      toast.error('Failed to delete rule. Please try again.');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await axios.put(`${API_BASE_URL}/api/taxonomy/rules/${rule.id}`, {
        active: !rule.active
      });
      fetchRules();
    } catch (err) {
      console.error('Failed to update rule:', err);
      toast.error('Failed to update rule. Please try again.');
    }
  };

  if (loading) {
    // K-S (UI-Durchgang 2): Seitenaufbau als Geruest der geladenen Regeltabelle
    // (5 Spalten), nicht als Kreisel.
    return (
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <TableSkeleton columns={5} rows={6} ariaLabel="Loading rules" />
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Rules Engine
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Database-driven taxonomy rules with configurable signal weights and blockers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Show help information">
            <IconButton onClick={() => setShowHelp(!showHelp)} size="small">
              <HelpOutline />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setRuleDialog({ open: true, mode: 'create', rule: null })}
          >
            New rule
          </Button>
        </Box>
      </Box>

      {/* Help Section */}
      <Collapse in={showHelp}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            How Rules Work
          </Typography>
          <Typography variant="body2" paragraph>
            Rules are evaluated during taxonomy enrichment to assign products to categories (use_case, brew_method, etc.).
            Each rule consists of three key components:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
            <li><strong>Target:</strong> The taxonomy field and value to assign (e.g., use_case = "travel")</li>
            <li><strong>Signals:</strong> Positive indicators that add points toward the threshold (e.g., form_factor = "portable" gives +2 points)</li>
            <li><strong>Threshold:</strong> Minimum score required for the rule to pass and assign the target value</li>
            <li><strong>Blockers:</strong> (Optional) Conditions that prevent the rule from passing, regardless of score</li>
          </Box>
          <Typography variant="body2" paragraph>
            <strong>Example:</strong> A "travel" rule might give +2 points for portable form_factor, +1 for battery_powered,
            and require a threshold of 2. If a product has both signals (3 points), it gets assigned use_case = "travel".
          </Typography>
          <Typography variant="body2">
            Rules are evaluated in priority order (higher numbers first). Use the test function to validate rules against specific products.
          </Typography>
        </Alert>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Rules
              </Typography>
              <Typography variant="h4">
                {rules.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Rules
              </Typography>
              <Typography variant="h4">
                {rules.filter(r => r.active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Evaluations
              </Typography>
              <Typography variant="h4">
                {rules.reduce((sum, r) => sum + (r.stats?.evaluation_count || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Match Rate
              </Typography>
              <Typography variant="h4">
                {rules.length > 0
                  ? Math.round(
                      (rules.reduce((sum, r) => sum + (r.stats?.match_rate || 0), 0) / rules.length) * 100
                    )
                  : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Rules Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rule Name</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Threshold</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No rules found. Click "New rule" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {rule.rule_name}
                      </Typography>
                      {rule.description && (
                        <Typography variant="caption" color="text.secondary">
                          {rule.description}
                        </Typography>
                      )}
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {rule.stats?.evaluation_count || 0} evaluations • {Math.round((rule.stats?.match_rate || 0) * 100)}% match rate
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${rule.target_field} = ${rule.target_value}`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      ≥ {rule.threshold}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.active}
                      onChange={() => handleToggleActive(rule)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setRuleDialog({ open: true, mode: 'edit', rule })}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteRule(rule.id, rule.rule_name)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setSelectedRule(rule);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setTestDialog({ open: true, rule: selectedRule });
            setMenuAnchor(null);
          }}
        >
          Test rule
        </MenuItem>
        <MenuItem
          onClick={() => {
            setLogsDialog({ open: true, rule: selectedRule });
            setMenuAnchor(null);
          }}
        >
          View evaluation logs
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <RuleEditorDialog
        open={ruleDialog.open}
        mode={ruleDialog.mode}
        rule={ruleDialog.rule}
        onClose={() => setRuleDialog({ open: false, mode: 'create', rule: null })}
        onSuccess={fetchRules}
      />

      <RuleTesterDialog
        open={testDialog.open}
        rule={testDialog.rule}
        onClose={() => setTestDialog({ open: false, rule: null })}
      />

      <RuleLogsDialog
        open={logsDialog.open}
        rule={logsDialog.rule}
        onClose={() => setLogsDialog({ open: false, rule: null })}
      />

      <ConfirmDialog
        open={!!pendingDeleteRule}
        title={`Delete rule "${pendingDeleteRule?.name ?? ''}"?`}
        message="This will also delete all evaluation logs."
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDeleteRule}
        onCancel={() => setPendingDeleteRule(null)}
      />
    </Box>
  );
}

/**
 * Rule Editor Dialog (Create/Edit)
 */
function RuleEditorDialog({ open, mode, rule, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    rule_name: '',
    description: '',
    target_field: 'use_case',
    target_value: '',
    threshold: 2,
    priority: 100,
    active: true,
  });
  const [signals, setSignals] = useState([]);  // Array of {field, operator, value, weight}
  const [blockers, setBlockers] = useState([]); // Array of {field, operator, value}
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState(null);
  // 🔴 K-F1 (2026-10-10): der Knopf sperrt sich beim Speichern und verliert
  // dabei den Fokus — der Haken holt ihn danach zurueck.
  const speichernKnopf = useRef(null);
  useFokusNachLaden(speichernKnopf, saving);

  useEffect(() => {
    if (open && mode === 'edit' && rule) {
      setFormData({
        rule_name: rule.rule_name,
        description: rule.description || '',
        target_field: rule.target_field,
        target_value: rule.target_value,
        threshold: rule.threshold,
        priority: rule.priority,
        active: rule.active,
      });

      // Convert signal_weights JSON to visual format
      const signalList = [];
      Object.entries(rule.signal_weights || {}).forEach(([path, conditions]) => {
        const [field, operator] = path.split('.');
        Object.entries(conditions).forEach(([value, weight]) => {
          signalList.push({ field, operator, value, weight });
        });
      });
      setSignals(signalList);

      // Convert blockers JSON to visual format
      const blockerList = [];
      Object.entries(rule.blockers || {}).forEach(([path, values]) => {
        const [field, operator] = path.split('.');
        values.forEach(value => {
          blockerList.push({ field, operator, value });
        });
      });
      setBlockers(blockerList);
    } else if (open && mode === 'create') {
      setFormData({
        rule_name: '',
        description: '',
        target_field: 'use_case',
        target_value: '',
        threshold: 2,
        priority: 100,
        active: true,
      });
      setSignals([]);
      setBlockers([]);
    }
    setValidationError(null);
    setShowAdvanced(false);
  }, [open, mode, rule]);

  const handleSave = async () => {
    // Validate required fields
    if (!formData.rule_name || !formData.target_field || !formData.target_value) {
      setValidationError('Rule name, target field, and target value are required');
      return;
    }

    // Convert signals to signal_weights JSON format
    const signal_weights = {};
    signals.forEach(({ field, operator, value, weight }) => {
      const path = `${field}.${operator}`;
      if (!signal_weights[path]) {
        signal_weights[path] = {};
      }
      signal_weights[path][value] = parseInt(weight);
    });

    // Convert blockers to JSON format
    const blockersJson = {};
    blockers.forEach(({ field, operator, value }) => {
      const path = `${field}.${operator}`;
      if (!blockersJson[path]) {
        blockersJson[path] = [];
      }
      if (!blockersJson[path].includes(value)) {
        blockersJson[path].push(value);
      }
    });

    setSaving(true);
    try {
      const payload = {
        ...formData,
        signal_weights,
        blockers: blockersJson,
      };

      if (mode === 'create') {
        await axios.post(`${API_BASE_URL}/api/taxonomy/rules`, payload);
      } else {
        await axios.put(`${API_BASE_URL}/api/taxonomy/rules/${rule.id}`, payload);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save rule:', err);
      setValidationError(err.response?.data?.error || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create New Rule' : `Edit Rule: ${rule?.rule_name}`}
      </DialogTitle>
      <DialogContent>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <TextField
              label="Rule Name"
              fullWidth
              value={formData.rule_name}
              onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Target Field"
              fullWidth
              value={formData.target_field}
              onChange={(e) => setFormData({ ...formData, target_field: e.target.value })}
              required
              helperText="e.g., use_case, brew_method_simple"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Target Value"
              fullWidth
              value={formData.target_value}
              onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
              required
              helperText="e.g., travel, pro, office"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Threshold"
              type="number"
              fullWidth
              value={formData.threshold}
              onChange={(e) => setFormData({ ...formData, threshold: parseInt(e.target.value) })}
              required
              helperText="Minimum score for rule to pass"
            />
          </Grid>

          {/* Signal Builder */}
          <Grid size={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Signals (positive indicators)
              </Typography>
              <Button
                size="small"
                startIcon={<AddCircleOutline />}
                onClick={() => setSignals([...signals, { field: 'form_factor', operator: 'equals', value: '', weight: 2 }])}
              >
                Add signal
              </Button>
            </Box>
            {signals.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No signals added. Click "Add signal" to create one.
              </Typography>
            ) : (
              signals.map((signal, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Field</InputLabel>
                        <Select
                          value={signal.field}
                          label="Field"
                          onChange={(e) => {
                            const newSignals = [...signals];
                            newSignals[idx].field = e.target.value;
                            setSignals(newSignals);
                          }}
                        >
                          <MenuItem value="form_factor">Form Factor</MenuItem>
                          <MenuItem value="canonical_type">Canonical Type</MenuItem>
                          <MenuItem value="occasion_fit">Occasion Fit</MenuItem>
                          <MenuItem value="system_memberships">System Memberships</MenuItem>
                          <MenuItem value="power_requirement">Power Requirement</MenuItem>
                          <MenuItem value="skill_level">Skill Level</MenuItem>
                          <MenuItem value="role">Role</MenuItem>
                          <MenuItem value="brew_focus">Brew Focus</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={signal.operator}
                          label="Operator"
                          onChange={(e) => {
                            const newSignals = [...signals];
                            newSignals[idx].operator = e.target.value;
                            setSignals(newSignals);
                          }}
                        >
                          <MenuItem value="equals">Equals</MenuItem>
                          <MenuItem value="contains">Contains</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Value"
                        value={signal.value}
                        onChange={(e) => {
                          const newSignals = [...signals];
                          newSignals[idx].value = e.target.value;
                          setSignals(newSignals);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Weight"
                        type="number"
                        value={signal.weight}
                        onChange={(e) => {
                          const newSignals = [...signals];
                          newSignals[idx].weight = e.target.value;
                          setSignals(newSignals);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 1 }}>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`Remove signal ${idx + 1}`}
                        onClick={() => setSignals(signals.filter((_, i) => i !== idx))}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))
            )}
          </Grid>

          {/* Advanced Section */}
          <Grid size={12}>
            <Button
              size="small"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </Button>
          </Grid>

          {showAdvanced && (
            <>
              <Grid size={12}>
                <TextField
                  label="Priority"
                  type="number"
                  fullWidth
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  helperText="Higher = evaluated first (default: 100)"
                />
              </Grid>

              {/* Blocker Builder */}
              <Grid size={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Blockers (conditions that prevent rule from passing)
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutline />}
                    onClick={() => setBlockers([...blockers, { field: 'form_factor', operator: 'equals', value: '' }])}
                  >
                    Add blocker
                  </Button>
                </Box>
                {blockers.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No blockers added. Blockers are optional.
                  </Typography>
                ) : (
                  blockers.map((blocker, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Field</InputLabel>
                            <Select
                              value={blocker.field}
                              label="Field"
                              onChange={(e) => {
                                const newBlockers = [...blockers];
                                newBlockers[idx].field = e.target.value;
                                setBlockers(newBlockers);
                              }}
                            >
                              <MenuItem value="form_factor">Form Factor</MenuItem>
                              <MenuItem value="canonical_type">Canonical Type</MenuItem>
                              <MenuItem value="occasion_fit">Occasion Fit</MenuItem>
                              <MenuItem value="system_memberships">System Memberships</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={blocker.operator}
                              label="Operator"
                              onChange={(e) => {
                                const newBlockers = [...blockers];
                                newBlockers[idx].operator = e.target.value;
                                setBlockers(newBlockers);
                              }}
                            >
                              <MenuItem value="equals">Equals</MenuItem>
                              <MenuItem value="contains">Contains</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Value"
                            value={blocker.value}
                            onChange={(e) => {
                              const newBlockers = [...blockers];
                              newBlockers[idx].value = e.target.value;
                              setBlockers(newBlockers);
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 1 }}>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Remove blocker ${idx + 1}`}
                            onClick={() => setBlockers(blockers.filter((_, i) => i !== idx))}
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))
                )}
              </Grid>
            </>
          )}
          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {/* 🔴 K-F1 (2026-10-10): der Lauf zeigt sich am Knopf selbst — Ring
            statt Wortwechsel. `loadingPosition="start"` haelt die
            Beschriftung lesbar (die Vorgabe `center` setzt `color:
            transparent`). Die RUHE-Beschriftung bleibt stehen. */}
        <Button
          ref={speichernKnopf}
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          loading={saving}
          loadingPosition="start"
          aria-busy={saving || undefined}
        >
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Rule Tester Dialog
 */
function RuleTesterDialog({ open, rule, onClose }) {
  const [productId, setProductId] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  // 🔴 K-F1 (2026-10-10): der Knopf sperrt sich beim Testen und verliert dabei
  // den Fokus — der Haken holt ihn danach zurueck.
  const testKnopf = useRef(null);
  useFokusNachLaden(testKnopf, testing);

  const handleTest = async () => {
    if (!productId.trim()) {
      toast.error('Please enter a product ID');
      return;
    }

    setTesting(true);
    setResult(null);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/taxonomy/rules/${rule.id}/test`, {
        product_id: productId.trim()
      });
      setResult(res.data);
    } catch (err) {
      console.error('Failed to test rule:', err);
      toast.error(err.response?.data?.error || 'Failed to test rule');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Test Rule: {rule?.rule_name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Product ID"
            fullWidth
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            placeholder="Enter product ID (e.g., 8765432109876)"
            onKeyPress={(e) => e.key === 'Enter' && handleTest()}
          />
          {/* 🔴 K-F1 (2026-10-10): der Lauf zeigt sich am Knopf selbst — Ring
              statt Wortwechsel. `loadingPosition="start"` haelt die
              Beschriftung lesbar (die Vorgabe `center` setzt `color:
              transparent`). Die RUHE-Beschriftung bleibt stehen. */}
          <Button
            ref={testKnopf}
            variant="contained"
            onClick={handleTest}
            disabled={testing}
            loading={testing}
            loadingPosition="start"
            aria-busy={testing || undefined}
            sx={{ mt: 2 }}
            fullWidth
          >
            Test rule
          </Button>

          {result && (
            <Box sx={{ mt: 3 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Product: {result.product?.product_name}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {result.result.passed ? (
                    <Chip icon={<Check />} label="PASSED" color="success" />
                  ) : (
                    <Chip icon={<Close />} label="FAILED" color="error" />
                  )}
                  <Typography variant="body2">
                    Score: {result.result.score} / {result.result.threshold}
                  </Typography>
                  <Typography variant="body2">
                    Confidence: {Math.round(result.result.confidence * 100)}%
                  </Typography>
                </Box>

                {result.result.matched_signals?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Matched Signals
                    </Typography>
                    {result.result.matched_signals.map((signal, idx) => (
                      <Chip
                        key={idx}
                        label={`${signal.source}: ${signal.value} (+${signal.weight})`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}

                {result.result.blocked_by?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Blocked By
                    </Typography>
                    {result.result.blocked_by.map((blocker, idx) => (
                      <Alert key={idx} severity="warning" sx={{ mb: 1 }}>
                        {blocker.reason}
                      </Alert>
                    ))}
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Execution time: {formatNumber(result.result.execution_time_ms)}ms
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Rule Logs Dialog
 */
function RuleLogsDialog({ open, rule, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && rule) {
      fetchLogs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchLogs defined after this effect
  }, [open, rule]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/taxonomy/rules/${rule.id}/logs?limit=20`);
      setLogs(res.data.logs);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Evaluation Logs: {rule?.rule_name}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No evaluation logs found for this rule.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Evaluated At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography variant="body2">{log.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.product_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.passed ? 'PASS' : 'FAIL'}
                        size="small"
                        color={log.passed ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {log.score} / {log.threshold}
                    </TableCell>
                    <TableCell>
                      {Math.round(log.confidence * 100)}%
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDateTime(log.evaluated_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
